import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { API } from '../services/api';
import { MainLayout } from '../components/shared/MainLayout';
import { toast } from 'sonner';
import {
  ArrowClockwise, DownloadSimple, EnvelopeSimple, Users, Flame,
  CheckCircle, WarningCircle, TrendUp, Eye, Target, Gauge, Heartbeat,
} from '@phosphor-icons/react';

const fmtPct = (v) => (typeof v === 'number' ? `${v.toFixed(1)}%` : '—');
const fmtNum = (v) => (typeof v === 'number' ? v.toLocaleString('en-AU') : '—');

/** Founder Product Intelligence dashboard — Phase 6. */
const FounderIntelligencePage = () => {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [timeline, setTimeline] = useState(null);

  const fetchData = async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const { data: d } = await axios.get(`${API}/founder/intelligence?days=${days}`);
      setData(d);
    } catch (err) {
      toast.error('Failed to load intelligence');
      console.error(err);
    } finally {
      setLoading(false); setRefreshing(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [days]);

  const triggerDigest = async () => {
    try {
      toast.loading('Sending digest to your inbox…', { id: 'digest' });
      const { data: r } = await axios.post(`${API}/founder/intelligence/controls/trigger-digest`);
      toast.dismiss('digest');
      if (r.sent) toast.success(`Digest sent · ${r.message_id || ''}`);
      else toast.error(`Failed: ${r.error || 'unknown'}`);
    } catch (e) { toast.dismiss('digest'); toast.error('Failed'); }
  };

  const resendOnboarding = async (userId, email) => {
    try {
      const { data: r } = await axios.post(`${API}/founder/intelligence/controls/resend-onboarding/${userId}`);
      if (r.sent) toast.success(`Welcome email resent → ${email}`);
      else toast.error(`Failed: ${r.error || 'unknown'}`);
    } catch (e) { toast.error('Failed'); }
  };

  const openTimeline = async (userId) => {
    try {
      const { data: t } = await axios.get(`${API}/founder/intelligence/user/${userId}/timeline`);
      setTimeline(t);
    } catch (e) { toast.error('Failed to load timeline'); }
  };

  const exportCsv = () => {
    window.open(`${API}/founder/intelligence/export.csv?days=${days}`, '_blank');
  };

  if (loading && !data) {
    return <MainLayout><div className="p-6 text-slate-500">Loading intelligence…</div></MainLayout>;
  }

  const acq = data?.acquisition || {};
  const act = data?.activation || {};
  const ret = data?.retention || {};
  const eng = data?.engagement || {};
  const val = data?.value || {};
  const risk = data?.risk || {};

  return (
    <MainLayout>
      <div className="max-w-5xl mx-auto pb-20" data-testid="founder-intelligence-page">

        {/* Header + controls */}
        <div className="flex items-center justify-between gap-3 mb-5">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Founder intelligence</p>
            <h1 className="text-[22px] font-semibold text-[#0E1C2E] tracking-[-0.01em]">What's working. What's failing.</h1>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={days} onChange={(e) => setDays(Number(e.target.value))}
              className="text-[12.5px] px-2 py-1.5 rounded-lg border border-[#ECE8E1] bg-white"
              data-testid="intel-window-select"
            >
              <option value={7}>7d</option>
              <option value={14}>14d</option>
              <option value={30}>30d</option>
              <option value={90}>90d</option>
            </select>
            <button onClick={() => fetchData(true)} className="p-2 rounded-lg border border-[#ECE8E1] bg-white hover:bg-[#FBFAF6]" data-testid="intel-refresh-btn">
              <ArrowClockwise size={14} className={refreshing ? 'animate-spin text-[#2FB3A0]' : 'text-slate-500'} />
            </button>
            <button onClick={exportCsv} className="p-2 rounded-lg border border-[#ECE8E1] bg-white hover:bg-[#FBFAF6]" data-testid="intel-export-btn" title="Export CSV">
              <DownloadSimple size={14} className="text-slate-500" />
            </button>
            <button onClick={triggerDigest} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0E1C2E] text-white text-[12.5px] font-semibold hover:bg-[#1A2B42]" data-testid="intel-trigger-digest-btn">
              <EnvelopeSimple size={13} /> Send digest
            </button>
          </div>
        </div>

        {/* Acquisition */}
        <Section title="Acquisition" eyebrow="Who's arriving" icon={Users}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Metric label="Registrations" value={fmtNum(acq.total_registrations)} />
            <Metric label="Demo starts" value={fmtNum(acq.demo?.started)} />
            <Metric label="Demo completion" value={fmtPct(acq.demo?.completion_pct)} />
            <Metric label="Demo → signup" value={fmtPct(acq.demo?.conversion_pct)} />
          </div>
          <DayBarChart rows={acq.registrations_by_day || []} label="Registrations by day" />
          {Array.isArray(acq.sources) && acq.sources.length > 0 && (
            <TablePills items={acq.sources.map(s => ({ k: s.source, v: s.count }))} />
          )}
        </Section>

        {/* Activation */}
        <Section title="Activation" eyebrow="What they do first" icon={Target}>
          <FunnelStages stages={act.stages || []} />
        </Section>

        {/* Retention */}
        <Section title="Retention" eyebrow="Are they coming back" icon={ArrowClockwise}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Metric label="D1" value={fmtPct(ret.d1_pct)} sub={`${ret.d1_count}/${ret.cohort_size}`} />
            <Metric label="D7" value={fmtPct(ret.d7_pct)} sub={`${ret.d7_count}/${ret.cohort_size}`} />
            <Metric label="D14" value={fmtPct(ret.d14_pct)} sub={`${ret.d14_count}/${ret.cohort_size}`} />
            <Metric label="Weekly active" value={fmtNum(ret.weekly_active_users)} />
            <Metric label="Returning" value={fmtNum(ret.returning_users)} />
            <Metric label="Sessions / wk" value={fmtNum(ret.avg_sessions_per_week)} />
          </div>
        </Section>

        {/* Engagement */}
        <Section title="Engagement" eyebrow="How deep they go" icon={Flame}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Metric label="Check-in WAU %" value={fmtPct(eng.checkin_weekly_pct)} sub={`${eng.checkin_weekly_users}/${eng.total_real_users}`} />
            <Metric label="Prep emails sent" value={fmtNum(eng.prep_email?.sent)} />
            <Metric label="Prep open rate" value={fmtPct(eng.prep_email?.open_pct)} />
            <Metric label="Prep review rate" value={fmtPct(eng.prep_email?.review_pct)} />
          </div>
          {eng.top_starter_prompts?.length > 0 && (
            <RankedList title="Top first messages" items={eng.top_starter_prompts.map(p => ({ label: p.text, n: p.count }))} />
          )}
          {eng.top_dismissed_cards?.length > 0 && (
            <RankedList title="Most-dismissed cards" items={eng.top_dismissed_cards.map(p => ({ label: p.key, n: p.count }))} />
          )}
        </Section>

        {/* Value */}
        <Section title="Value signals" eyebrow="Users finding value" icon={Heartbeat}>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Metric label="3+ check-ins" value={fmtNum(val.three_plus_checkins)} />
            <Metric label="2+ chats" value={fmtNum(val.two_plus_chats)} />
            <Metric label="Uploaded a report" value={fmtNum(val.uploaded_report)} />
            <Metric label="Prep reviewed" value={fmtNum(val.prep_reviewed)} />
            <Metric label="3+ day streak" value={fmtNum(val.streak_users)} />
          </div>
        </Section>

        {/* Risk */}
        <Section title="Risk signals" eyebrow="Who needs saving" icon={WarningCircle}>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
            <Metric label="Never returned" value={fmtNum(risk.never_returned_count)} tone="risk" />
            <Metric label="Stopped after day 1" value={fmtNum(risk.stopped_day_one_count)} tone="risk" />
            <Metric label="No-response chats" value={fmtNum(risk.no_response_chats_count)} tone="risk" />
          </div>
          <RiskUsersTable
            title="Never returned (past 48h)"
            users={risk.never_returned || []}
            onResend={resendOnboarding}
            onOpenTimeline={openTimeline}
          />
          <RiskUsersTable
            title="Stopped after day 1"
            users={risk.stopped_day_one || []}
            onResend={resendOnboarding}
            onOpenTimeline={openTimeline}
          />
          {risk.slowest_routes?.length > 0 && (
            <div className="mt-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2">Slowest routes (p75)</p>
              <div className="rounded-xl border border-[#ECE8E1] overflow-hidden">
                <table className="w-full text-[13px]">
                  <thead className="bg-[#FBFAF6] text-slate-500 text-[11px] uppercase tracking-[0.08em]">
                    <tr><th className="text-left px-3 py-2">Route</th><th className="text-right px-3 py-2">LCP</th><th className="text-right px-3 py-2">INP</th><th className="text-right px-3 py-2">CLS</th><th className="text-right px-3 py-2">n</th></tr>
                  </thead>
                  <tbody>
                    {risk.slowest_routes.map((r, i) => (
                      <tr key={i} className="border-t border-[#ECE8E1]">
                        <td className="px-3 py-2 font-medium text-[#0E1C2E]">{r.route}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.lcp_p75 || '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.inp_p75 || '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.cls_p75 ? r.cls_p75.toFixed(2) : '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-400">{r.samples}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Section>

        {/* Impersonate demo */}
        <div className="mt-6 flex items-center justify-between gap-3 rounded-xl bg-[#FBFAF6] border border-[#ECE8E1] px-4 py-3">
          <p className="text-[13px] text-slate-600">See what a prospect sees in the demo flow</p>
          <button
            onClick={() => navigate('/demo')}
            className="text-[13px] font-semibold text-[#0E1C2E] inline-flex items-center gap-1.5"
            data-testid="intel-impersonate-demo-btn"
          >
            Open demo <Eye size={13} weight="bold" />
          </button>
        </div>

        <p className="mt-8 text-[11px] text-slate-400 text-center">
          Generated {data?.generated_at ? new Date(data.generated_at).toLocaleString() : '—'} ·
          window {data?.window_days}d
        </p>
      </div>

      {/* Timeline drawer */}
      {timeline && <TimelineDrawer data={timeline} onClose={() => setTimeline(null)} />}
    </MainLayout>
  );
};

/* ═══════════════════════ Subcomponents ═══════════════════════ */

const Section = ({ title, eyebrow, icon: Icon, children }) => (
  <section className="mb-8" data-testid={`intel-section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-2 mb-3">
      <div className="h-7 w-7 rounded-full bg-[#0E1C2E]/5 flex items-center justify-center"><Icon size={13} className="text-[#0E1C2E]" /></div>
      <div>
        <p className="text-[10.5px] uppercase tracking-[0.18em] text-slate-400 font-semibold">{eyebrow}</p>
        <h2 className="text-[16px] font-semibold text-[#0E1C2E] tracking-[-0.005em] leading-tight">{title}</h2>
      </div>
    </div>
    <div>{children}</div>
  </section>
);

const Metric = ({ label, value, sub, tone }) => (
  <div className={`rounded-xl border px-3.5 py-3 ${tone === 'risk' ? 'border-[#F3D8C7] bg-[#FDF4EE]' : 'border-[#ECE8E1] bg-white'}`} data-testid="intel-metric">
    <p className="text-[11px] text-slate-500">{label}</p>
    <p className={`mt-0.5 text-[20px] font-semibold tabular-nums ${tone === 'risk' ? 'text-[#B95E3B]' : 'text-[#0E1C2E]'}`}>{value}</p>
    {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
  </div>
);

const DayBarChart = ({ rows, label }) => {
  const max = useMemo(() => Math.max(1, ...rows.map(r => r.count)), [rows]);
  if (!rows?.length) return null;
  return (
    <div className="mt-4">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2">{label}</p>
      <div className="flex items-end gap-1 h-20 border-b border-[#ECE8E1]">
        {rows.map((r, i) => (
          <div key={r.date} className="flex-1 group relative">
            <div className="w-full bg-[#0E1C2E] rounded-t" style={{ height: `${(r.count / max) * 100}%` }} />
            <div className="absolute -top-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 text-[10px] bg-[#0E1C2E] text-white px-1.5 py-0.5 rounded whitespace-nowrap">{r.date} · {r.count}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const FunnelStages = ({ stages }) => (
  <div className="space-y-2" data-testid="intel-funnel">
    {stages.map((s, i) => (
      <div key={s.stage} className="rounded-lg border border-[#ECE8E1] bg-white overflow-hidden">
        <div className="relative flex items-center justify-between px-3.5 py-2.5">
          <div className="absolute inset-y-0 left-0 bg-[#2FB3A0]/10" style={{ width: `${s.pct}%` }} />
          <p className="relative text-[13px] font-medium text-[#0E1C2E]">{s.stage.replace(/_/g, ' ')}</p>
          <div className="relative flex items-center gap-3">
            <span className="text-[12px] text-slate-500 tabular-nums">{s.count}</span>
            <span className="text-[13px] font-semibold text-[#0E1C2E] tabular-nums">{s.pct.toFixed(1)}%</span>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const TablePills = ({ items }) => (
  <div className="mt-3 flex flex-wrap gap-2">
    {items.map((x, i) => (
      <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-[#ECE8E1] text-[12px] text-slate-600">
        {x.k} <span className="text-slate-400 tabular-nums">· {x.v}</span>
      </span>
    ))}
  </div>
);

const RankedList = ({ title, items }) => (
  <div className="mt-4">
    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2">{title}</p>
    <div className="rounded-xl border border-[#ECE8E1] overflow-hidden">
      {items.slice(0, 8).map((x, i) => (
        <div key={i} className="flex items-start justify-between gap-3 px-3.5 py-2 border-t first:border-t-0 border-[#ECE8E1] text-[13px]">
          <p className="text-slate-600 flex-1 leading-snug">{x.label}</p>
          <span className="text-slate-400 tabular-nums">{x.n}</span>
        </div>
      ))}
    </div>
  </div>
);

const RiskUsersTable = ({ title, users, onResend, onOpenTimeline }) => {
  if (!users?.length) return null;
  return (
    <div className="mt-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2">{title} <span className="text-slate-400">· {users.length}</span></p>
      <div className="rounded-xl border border-[#ECE8E1] overflow-hidden bg-white">
        {users.slice(0, 6).map((u) => (
          <div key={u.id} className="flex items-center justify-between gap-3 px-3.5 py-2.5 border-t first:border-t-0 border-[#ECE8E1]" data-testid="risk-user-row">
            <div className="min-w-0 flex-1">
              <p className="text-[13px] text-[#0E1C2E] truncate">{u.name || u.email}</p>
              <p className="text-[11px] text-slate-400">{u.email}</p>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button onClick={() => onOpenTimeline(u.id)} className="text-[12px] text-slate-500 hover:text-[#0E1C2E] px-2 py-1" data-testid="risk-user-timeline-btn">Timeline</button>
              <button onClick={() => onResend(u.id, u.email)} className="text-[12px] font-semibold text-[#0E1C2E] hover:underline px-2 py-1" data-testid="risk-user-resend-btn">Resend welcome</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const TimelineDrawer = ({ data, onClose }) => (
  <div className="fixed inset-0 bg-[#0E1C2E]/40 z-50 flex items-end md:items-center md:justify-center" onClick={onClose} data-testid="intel-timeline-drawer">
    <div className="bg-white w-full md:max-w-xl md:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
      <div className="sticky top-0 bg-white border-b border-[#ECE8E1] px-5 py-4 flex items-center justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">User timeline</p>
          <p className="text-[16px] font-semibold text-[#0E1C2E]">{data.user.name || data.user.email}</p>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-[#0E1C2E] text-xl leading-none">×</button>
      </div>
      <div className="px-5 py-4">
        <div className="flex items-center gap-4 mb-4">
          <Metric label="Health score" value={`${data.health_score}/100`} />
          <Metric label="Streak" value={`${data.streak_days} day${data.streak_days === 1 ? '' : 's'}`} />
          <Metric label="Plan" value={data.user.plan_type || '—'} />
        </div>
        <Subsection title="Recent events">
          {(data.recent_events || []).slice(0, 15).map((e, i) => (
            <Row key={i} left={e.event} right={new Date(e.created_at).toLocaleString()} />
          ))}
        </Subsection>
        <Subsection title="Recent chats">
          {(data.recent_chats || []).slice(0, 8).map((c, i) => (
            <Row key={i} left={c.title || 'Untitled'} right={c.last_activity ? new Date(c.last_activity).toLocaleDateString() : '—'} />
          ))}
        </Subsection>
        <Subsection title="Recent appointments">
          {(data.recent_appointments || []).slice(0, 8).map((a, i) => (
            <Row key={i} left={a.title} right={`${a.date} ${a.prep_reviewed_at ? '✓' : ''}`} />
          ))}
        </Subsection>
      </div>
    </div>
  </div>
);

const Subsection = ({ title, children }) => (
  <div className="mb-4">
    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1.5">{title}</p>
    <div className="rounded-xl border border-[#ECE8E1] overflow-hidden">{children || <div className="px-3.5 py-2 text-[12px] text-slate-400">None</div>}</div>
  </div>
);

const Row = ({ left, right }) => (
  <div className="flex items-center justify-between gap-3 px-3.5 py-2 border-t first:border-t-0 border-[#ECE8E1] text-[12.5px]">
    <span className="text-slate-700 truncate flex-1">{left}</span>
    <span className="text-slate-400 tabular-nums flex-shrink-0">{right}</span>
  </div>
);

export default FounderIntelligencePage;
